import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Breadcrumbs } from '@components';
import { DoubleCheckModal } from '@shared/ui';
import {
  ProductForm,
  useCreateProduct,
  useProduct,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
} from '@modules/products';
import { useCategories } from '@modules/categories';
import type { CategorySelectionItem } from '@modules/products/components/CategorySelection';
import '@shared/styles/page.css';
import './CreateProductPage.css';

function buildCreatePayload(data: any, variantOfProductId?: number) {
  const categoryIds =
    data.categorySelections
      ?.filter((selection: any) => selection.categoryId && selection.categoryId !== '')
      .map((selection: any) => Number(selection.categoryId)) || [];

  const toNumberArray = (arr: any[] | undefined): number[] | undefined => {
    if (!arr || arr.length === 0) return undefined;
    return arr
      .map((id) => (typeof id === 'string' ? Number(id) : id))
      .filter((id) => !Number.isNaN(id));
  };

  return {
    name: data.name,
    description: data.description,
    price: Number(data.price),
    categoryIds,
    isNotFlower: Boolean(data.isNotFlower ?? false),
    composition: data.composition?.filter((item: any) => item?.value?.trim()).length
      ? data.composition
        .filter((item: any) => item?.value?.trim())
        .map((item: any) => ({
          value: String(item.value).trim(),
          count: Number(item.count) || 0,
        }))
      : undefined,
    keywords: data.keywords && data.keywords.length > 0 ? data.keywords : undefined,
    height: data.height ? Number(data.height) : undefined,
    width: data.width ? Number(data.width) : undefined,
    weight: data.weight ? Number(data.weight) : undefined,
    flowerCount: data.flowerCount ? Number(data.flowerCount) : undefined,
    flowerTypeIds: toNumberArray(data.flowerTypeIds),
    assemblyTypeId:
      data.assemblyTypeId && data.assemblyTypeId !== null
        ? Number(data.assemblyTypeId)
        : undefined,
    assemblyTimeMinutes:
      data.assemblyTimeMinutes != null &&
        data.assemblyTimeMinutes !== '' &&
        Number(data.assemblyTimeMinutes) > 0
        ? Number(data.assemblyTimeMinutes)
        : undefined,
    colorIds: toNumberArray(data.colorIds),
    variantInfo: variantOfProductId
      ? { variantOfProductId }
      : undefined,
  };
}

function extractImageFiles(images: unknown): File[] | undefined {
  if (!images) return undefined;
  if (Array.isArray(images)) {
    return images.length > 0 ? (images as File[]) : undefined;
  }
  if (images instanceof File) return [images];
  return undefined;
}

export default function CreateProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const variantOfProductIdParam = searchParams.get('variantOf');
  const variantOfProductId = variantOfProductIdParam
    ? Number(variantOfProductIdParam)
    : undefined;
  const categoryIdParam = searchParams.get('categoryId');
  const sectionIdParam = searchParams.get('sectionId');
  const categoryId = categoryIdParam ? Number(categoryIdParam) : undefined;
  const sectionId = sectionIdParam ? Number(sectionIdParam) : undefined;

  const { data: categories = [] } = useCategories();
  const { data: variantOfProduct, isLoading: variantLoading } = useProduct(
    variantOfProductId ?? 0,
  );
  const createProduct = useCreateProduct();

  const initialValues = useMemo(() => {
    if (variantOfProduct) {
      const categorySelections: CategorySelectionItem[] =
        variantOfProduct.categories && variantOfProduct.categories.length > 0
          ? variantOfProduct.categories.map((pc) => {
            const category = pc.category as any;
            const sid =
              category?.sectionId || category?.sections?.[0]?.sectionId || '';
            return {
              sectionId: sid as number | '',
              categoryId: pc.categoryId as number | '',
            };
          })
          : [{ sectionId: '' as const, categoryId: '' as const }];

      return {
        categorySelections,
        name: variantOfProduct.name || '',
        description: (variantOfProduct.description || '').slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH),
        price: variantOfProduct.price,
        composition:
          Array.isArray(variantOfProduct.composition) &&
            variantOfProduct.composition.length > 0
            ? variantOfProduct.composition
            : [{ value: '', count: 0 }],
        keywords: (variantOfProduct.keywords || []).slice(0, 20),
        isNotFlower: variantOfProduct.isNotFlower ?? false,
        height: variantOfProduct.height || undefined,
        width: variantOfProduct.width || undefined,
        weight: variantOfProduct.weight || undefined,
        flowerCount: variantOfProduct.flowerCount || undefined,
        flowerTypeIds: variantOfProduct.flowerTypes
          ? variantOfProduct.flowerTypes.map((pft) => pft.flowerTypeId)
          : variantOfProduct.flowerTypeId
            ? [variantOfProduct.flowerTypeId]
            : [],
        assemblyTypeId: variantOfProduct.assemblyTypeId ?? null,
        assemblyTimeMinutes: variantOfProduct.assemblyTimeMinutes ?? undefined,
        colorIds: variantOfProduct.colors
          ? variantOfProduct.colors.map((pc) => pc.colorId)
          : variantOfProduct.colorId
            ? [variantOfProduct.colorId]
            : [],
        images: [],
      };
    }

    if (categoryId) {
      const category = categories.find((c) => c.id === categoryId);
      if (category) {
        const finalSectionId = sectionId || category.sectionId || '';
        return {
          isNotFlower: false,
          categorySelections: [
            {
              sectionId: finalSectionId as number | '',
              categoryId: categoryId as number | '',
            },
          ],
        };
      }
    }

    if (sectionId) {
      return {
        isNotFlower: false,
        categorySelections: [
          {
            sectionId: sectionId as number | '',
            categoryId: '' as const,
          },
        ],
      };
    }

    return undefined;
  }, [variantOfProduct, categories, categoryId, sectionId]);

  const handleSubmit = async (data: any) => {
    try {
      const createData = buildCreatePayload(data, variantOfProductId);
      const files = extractImageFiles(data.images);
      const createdProduct = await createProduct.mutateAsync({ data: createData, files });

      notifications.show({
        title: 'Успешно',
        message: variantOfProductId
          ? 'Вариант товара успешно создан'
          : 'Товар успешно создан',
        color: 'green',
      });

      navigate(`/products/${createdProduct.id}`, {
        state: {
          from: (location.state as { from?: string } | null)?.from ?? '/products',
          openStocksModalOnLoad: true,
          showModerationInfoAfterStocks: true,
          ...(variantOfProductId ? { openVariantsModalOnLoad: true } : {}),
        },
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Произошла ошибка при создании товара';
      notifications.show({
        title: 'Ошибка',
        message: errorMessage,
        color: 'red',
      });
    }
  };

  const title =
    variantOfProductId && variantOfProduct
      ? `Вариант: ${variantOfProduct.name}`
      : 'Новый товар';

  const backTo =
    (location.state as { from?: string } | null)?.from ?? '/products';

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    navigate(backTo);
  };

  return (
    <div className="page-container create-product-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Товары', path: '/products' },
          { label: 'Создание' },
        ]}
      />

      <div className="page-header create-product-page__header">
        <div className="create-product-page__title-row">
          <h1>{title}</h1>
          <button
            type="button"
            className="create-product-page__cancel"
            onClick={() => setCancelConfirmOpen(true)}
          >
            Отмена
          </button>
        </div>
      </div>

      <div className="content-wrapper create-product-page__content">
        {variantOfProductId && variantLoading ? (
          <div className="empty-container">
            <Loader />
          </div>
        ) : !variantOfProductId || variantOfProduct ? (
          <ProductForm
            wizard
            onSubmit={handleSubmit}
            initialValues={initialValues}
            isLoading={createProduct.isPending}
          />
        ) : (
          <div className="empty-container">
            <p>Товар для варианта не найден</p>
          </div>
        )}
      </div>

      <DoubleCheckModal
        isOpen={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        title="Прекратить создание?"
        message="Точно ли вы хотите прекратить создание? Изменения не сохранятся."
        firstButtonText="Продолжить"
        secondButtonText="Прекратить"
        secondButtonAction={confirmCancel}
      />
    </div>
  );
}
